import { useEffect, useRef, useState } from "react";
import type { PlaybackDescriptor } from "../../../shared/src/playable-media";
import { pageVideoLink, pageVideoPlayerUrl } from "../../../shared/src/page-video";

/** The decorative audio motif (ui/src/audio-waveform.ts), drawn by React. */
function AudioWaveform() {
  return (
    <svg
      className="drate-audio-waveform"
      viewBox="0 0 400 88"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      {Array.from({ length: 80 }, (_, index) => {
        const height = Math.round(
          8 + 56 * Math.abs(Math.sin(index * 0.61) * Math.cos(index * 0.17)),
        );
        return (
          <rect
            key={index}
            x={index * 5}
            y={(88 - height) / 2}
            width="2"
            height={height}
            rx="1"
            fill="currentColor"
          />
        );
      })}
    </svg>
  );
}

/**
 * An uploaded recording in the site's own player. The server markup plays on
 * its own; the media-player element (ui/src/media-player.ts) enhances it once its code arrives,
 * and that code loads only on pages that have a player.
 *
 * A member's paid video can carry their name and code (`playback.watermark`, set when the creator
 * turned the visible watermark on): media-watermark.ts floats it over the picture once the player
 * is enhanced. Without one, nothing about the player changes.
 */
export function MediaPlayer({
  playback,
  artwork,
  skip = 15,
  loop = false,
  compact = false,
}: {
  playback: PlaybackDescriptor;
  artwork?: string | null;
  skip?: number;
  loop?: boolean;
  compact?: boolean;
}) {
  const player = useRef<HTMLElement>(null);
  const watermark = playback.kind === "video" ? playback.watermark?.text : undefined;
  useEffect(() => {
    if (!watermark) {
      void import("../../../ui/src/media-player");
      return;
    }
    // The watermark's code arrives first, so the enhanced player never draws without the mark,
    // or with its picture-in-picture button.
    let stopped = false;
    let unmount: (() => void) | undefined;
    void import("./media-watermark").then(async ({ mountWatermark }) => {
      await import("../../../ui/src/media-player");
      if (!stopped && player.current) unmount = mountWatermark(player.current, watermark);
    });
    return () => {
      stopped = true;
      unmount?.();
    };
  }, [watermark]);
  return (
    <drate-media-player
      ref={player}
      className={playback.kind === "audio" ? "drate-audio-player" : undefined}
      data-player={JSON.stringify(playback)}
      data-artwork={artwork || undefined}
      data-skip={skip}
      data-loop={loop || undefined}
    >
      {playback.kind === "video" ? (
        <video
          controls
          playsInline
          preload="none"
          src={playback.url || undefined}
          poster={artwork || playback.poster || undefined}
          aria-label={playback.title}
          // Picture in picture would show the video without the member's watermark.
          disablePictureInPicture={watermark ? true : undefined}
        >
          {playback.tracks.map((track, index) => (
            <track
              key={track.url}
              kind="captions"
              src={track.url}
              srcLang={track.language}
              label={track.label}
              default={index === 0}
            />
          ))}
        </video>
      ) : (
        <div className={compact ? "drate-audio-card drate-audio-compact" : "drate-audio-card"}>
          <AudioWaveform />
          {artwork || playback.poster ? (
            <img className="drate-art" src={artwork || playback.poster!} alt="" />
          ) : (
            <span className="drate-art drate-art-placeholder" aria-hidden="true">
              ♫
            </span>
          )}
          <div className="drate-audio-copy">
            <p>{playback.preview ? "Audio preview" : "Audio"}</p>
            <strong>{playback.title}</strong>
          </div>
          <audio
            className="drate-audio-fallback"
            controls
            preload="metadata"
            src={playback.url || undefined}
            aria-label={playback.title}
          />
        </div>
      )}
    </drate-media-player>
  );
}

/**
 * A linked YouTube, Vimeo or Twitch video. Nothing loads from the provider until
 * the visitor presses Play, unless the creator chose `autoplay`; the player is built from the
 * authored link again, so only a known provider's address reaches the frame, and Twitch is told
 * which site it plays on.
 */
export function PageVideo({
  url,
  title,
  attributes,
  badge,
  also,
  autoplay = false,
}: {
  url: string;
  title: string;
  attributes: Record<string, unknown>;
  badge?: { label: string; live?: boolean };
  also?: { label: string; href: string };
  autoplay?: boolean;
}) {
  const [playing, setPlaying] = useState<"pressed" | "page" | null>(null);
  // A player that loads with the page replaces the Play button once the page runs in the
  // browser, which knows the address Twitch has to be told.
  useEffect(() => {
    if (autoplay) setPlaying((current) => current ?? "page");
  }, [autoplay]);
  const video = pageVideoLink(url);
  if (!video) return null;
  const name = title || `${video.provider} video`;
  return (
    <drate-page-video
      {...attributes}
      className="drate-page-node drate-page-video"
      data-video-url={video.watchUrl}
      data-video-title={name}
    >
      <div className="drate-page-video-frame" data-video-frame="">
        {playing ? (
          <iframe
            ref={playing === "pressed" ? (frame) => frame?.focus() : undefined}
            src={pageVideoPlayerUrl(video, location.hostname, playing === "page")}
            title={name}
            allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
          />
        ) : (
          <button
            className="drate-page-video-load"
            type="button"
            data-load-video=""
            aria-label={`Play ${name} on ${video.provider}`}
            onClick={() => setPlaying("pressed")}
          >
            <span className="drate-page-video-play" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5.6v12.8a.8.8 0 0 0 1.2.7l10-6.4a.8.8 0 0 0 0-1.4l-10-6.4A.8.8 0 0 0 8 5.6Z" />
              </svg>
            </span>
            <span className="drate-page-video-copy">
              {badge && (
                <span className="drate-page-video-badge" data-live={badge.live ? "" : undefined}>
                  {badge.label}
                </span>
              )}
              {title && <strong>{title}</strong>}
              <span>Plays from {video.provider}</span>
            </span>
          </button>
        )}
      </div>
      <a
        className="drate-page-video-link"
        href={video.watchUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        Watch on {video.provider} <span aria-hidden="true">↗</span>
        <span className="sr-only"> (opens in a new tab)</span>
      </a>
      {also && (
        <a
          className="drate-page-video-link"
          href={also.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {also.label} <span aria-hidden="true">↗</span>
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
      )}
    </drate-page-video>
  );
}

declare module "react" {
  namespace JSX {
    interface IntrinsicElements {
      "drate-media-player": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > &
        Record<`data-${string}`, unknown>;
      "drate-page-video": React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> &
        Record<`data-${string}`, unknown>;
      "drate-audio-host": React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
    }
  }
}
